import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import type { AutomationGuide } from "@/hooks/useAutomationGuides";
import { useAutomationSupportRealtime } from "@/hooks/useKnowledgeHub";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  webSources?: Array<{ url: string; title: string; type: string }>;
  searchTier?: "official_docs" | "community" | "web_fallback";
  source_language?: "en" | "es";
  timestamp: Date;
}

interface GuideConversation {
  guideId: string;
  conversationId: string | null;
  messages: Message[];
}

interface AutomationSupportContextType {
  isOpen: boolean;
  isMinimized: boolean;
  selectedGuide: AutomationGuide | null;
  openSupport: (guide: AutomationGuide) => void;
  closeSupport: () => void;
  toggleMinimize: () => void;
  // Conversation management
  getCurrentConversation: () => GuideConversation | null;
  setConversationId: (guideId: string, conversationId: string) => void;
  addMessage: (guideId: string, message: Message) => void;
  removeMessage: (guideId: string, messageId: string) => void;
  clearConversation: (conversationId: string) => void;
  clearAllConversations: () => void;
}

const AutomationSupportContext =
  createContext<AutomationSupportContextType | null>(null);

export function AutomationSupportProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<AutomationGuide | null>(
    null,
  );
  // Store conversations per guide for history preservation
  const [conversations, setConversations] = useState<
    Map<string, GuideConversation>
  >(new Map());

  // Subscribe to realtime changes for conversation deletions (e.g., during guide regeneration)
  useAutomationSupportRealtime({ enabled: isOpen });

  const openSupport = useCallback((guide: AutomationGuide) => {
    setSelectedGuide(guide);
    setIsOpen(true);
    setIsMinimized(false);

    // Initialize conversation for this guide if not exists
    setConversations((prev) => {
      if (!prev.has(guide.id)) {
        const newMap = new Map(prev);
        newMap.set(guide.id, {
          guideId: guide.id,
          conversationId: null,
          messages: [],
        });
        return newMap;
      }
      return prev;
    });
  }, []);

  const closeSupport = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    setSelectedGuide(null);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const getCurrentConversation = useCallback((): GuideConversation | null => {
    if (!selectedGuide) return null;
    return conversations.get(selectedGuide.id) || null;
  }, [selectedGuide, conversations]);

  const setConversationId = useCallback(
    (guideId: string, conversationId: string) => {
      setConversations((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(guideId);
        if (existing) {
          newMap.set(guideId, { ...existing, conversationId });
        } else {
          newMap.set(guideId, { guideId, conversationId, messages: [] });
        }
        return newMap;
      });
    },
    [],
  );

  const addMessage = useCallback((guideId: string, message: Message) => {
    setConversations((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(guideId);
      if (existing) {
        newMap.set(guideId, {
          ...existing,
          messages: [...existing.messages, message],
        });
      } else {
        newMap.set(guideId, {
          guideId,
          conversationId: null,
          messages: [message],
        });
      }
      return newMap;
    });
  }, []);

  const removeMessage = useCallback((guideId: string, messageId: string) => {
    setConversations((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(guideId);
      if (existing) {
        newMap.set(guideId, {
          ...existing,
          messages: existing.messages.filter((m) => m.id !== messageId),
        });
      }
      return newMap;
    });
  }, []);

  // Clear a specific conversation by its database ID
  const clearConversation = useCallback((conversationId: string) => {
    setConversations((prev) => {
      const newMap = new Map(prev);
      // Find and clear the conversation with matching conversationId
      for (const [guideId, conv] of newMap.entries()) {
        if (conv.conversationId === conversationId) {
          newMap.set(guideId, {
            guideId,
            conversationId: null,
            messages: [],
          });
          break;
        }
      }
      return newMap;
    });
  }, []);

  // Clear all conversations
  const clearAllConversations = useCallback(() => {
    setConversations(new Map());
  }, []);

  const contextValue = useMemo(
    () => ({
      isOpen,
      isMinimized,
      selectedGuide,
      openSupport,
      closeSupport,
      toggleMinimize,
      getCurrentConversation,
      setConversationId,
      addMessage,
      removeMessage,
      clearConversation,
      clearAllConversations,
    }),
    [
      isOpen,
      isMinimized,
      selectedGuide,
      openSupport,
      closeSupport,
      toggleMinimize,
      getCurrentConversation,
      setConversationId,
      addMessage,
      removeMessage,
      clearConversation,
      clearAllConversations,
    ],
  );

  return (
    <AutomationSupportContext.Provider value={contextValue}>
      {children}
    </AutomationSupportContext.Provider>
  );
}

export function useAutomationSupport() {
  const context = useContext(AutomationSupportContext);
  if (!context) {
    throw new Error(
      "useAutomationSupport must be used within an AutomationSupportProvider",
    );
  }
  return context;
}

export type { Message as AutomationSupportMessage };
